import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { COOKIE_CONSENT_TTL_MS } from "@/lib/consent";
import type {
  PersistedTranscriptStoreState,
  StoredTranscriptMediaMeta,
  StoredTranscriptMediaRecord,
  TranscriptMediaStoreKey,
  TranscriptStore,
  TranscriptStoreState,
} from "@/types/transcript-store";

export const TRANSCRIPT_STORE_STORAGE_KEY = "vts-transcript-tool";

const mediaDbName = "vts-transcript-media";
const mediaDbVersion = 1;
const mediaStoreName = "media";
const uploadMediaKey: TranscriptMediaStoreKey = "upload";
const recordMediaKey: TranscriptMediaStoreKey = "record";
const recordedFilename = "recorded-video.webm";
const mediaPersistenceWarning =
  "This browser could not keep the selected media after refresh. The current page can still use it.";

function nextCacheExpiresAt(): number {
  return Date.now() + COOKIE_CONSENT_TTL_MS;
}

function initialTranscriptState(): TranscriptStoreState {
  return {
    mode: "upload",
    title: "",
    mediaUrl: "",
    selectedFile: null,
    selectedFileMeta: null,
    recordedBlob: null,
    recordedBlobMeta: null,
    recordPreviewUrl: "",
    uploadPreviewUrl: "",
    isRecording: false,
    isUploadPlaying: false,
    isUploadMuted: false,
    isRecordMuted: false,
    isHydratingMedia: false,
    query: "",
    validationError: "",
    storageWarning: "",
    result: null,
    summary: null,
    cacheExpiresAt: nextCacheExpiresAt(),
  };
}

function canUseObjectUrls(): boolean {
  return (
    typeof URL !== "undefined" &&
    typeof URL.createObjectURL === "function" &&
    typeof URL.revokeObjectURL === "function"
  );
}

function canUseIndexedDb(): boolean {
  return typeof indexedDB !== "undefined";
}

function createPreviewUrl(blob: Blob, kind: TranscriptMediaStoreKey): string {
  if (!canUseObjectUrls()) return "";
  if (kind === uploadMediaKey && !blob.type.startsWith("video/")) return "";
  return URL.createObjectURL(blob);
}

function revokePreviewUrl(url: string): void {
  if (!url || !canUseObjectUrls()) return;
  URL.revokeObjectURL(url);
}

function isCacheExpired(cacheExpiresAt?: number): boolean {
  return !cacheExpiresAt || cacheExpiresAt <= Date.now();
}

function mediaMetaFromBlob(
  blob: Blob,
  key: TranscriptMediaStoreKey,
  fallbackName: string,
): StoredTranscriptMediaMeta {
  const file = typeof File !== "undefined" && blob instanceof File ? blob : null;
  return {
    key,
    name: file?.name || fallbackName,
    type: blob.type,
    size: blob.size,
    lastModified: file?.lastModified,
    savedAt: Date.now(),
  };
}

function fileFromStoredMedia(record: StoredTranscriptMediaRecord): File {
  return new File([record.blob], record.name, {
    type: record.type || record.blob.type,
    lastModified: record.lastModified || record.savedAt,
  });
}

function openMediaDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!canUseIndexedDb()) {
      reject(new Error("IndexedDB is not available."));
      return;
    }

    const request = indexedDB.open(mediaDbName, mediaDbVersion);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(mediaStoreName)) {
        db.createObjectStore(mediaStoreName, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error || new Error("Unable to open transcript media cache."));
  });
}

async function putStoredMedia(record: StoredTranscriptMediaRecord): Promise<void> {
  if (!canUseIndexedDb()) return;

  const db = await openMediaDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(mediaStoreName, "readwrite");
    const store = transaction.objectStore(mediaStoreName);
    const request = store.put(record);

    request.onerror = () =>
      reject(request.error || new Error("Unable to save transcript media."));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error || new Error("Unable to save transcript media."));
  }).finally(() => db.close());
}

async function getStoredMedia(
  key: TranscriptMediaStoreKey,
): Promise<StoredTranscriptMediaRecord | null> {
  if (!canUseIndexedDb()) return null;

  const db = await openMediaDb();
  return new Promise<StoredTranscriptMediaRecord | null>((resolve, reject) => {
    const transaction = db.transaction(mediaStoreName, "readonly");
    const store = transaction.objectStore(mediaStoreName);
    const request = store.get(key);

    request.onsuccess = () =>
      resolve((request.result as StoredTranscriptMediaRecord | undefined) || null);
    request.onerror = () =>
      reject(request.error || new Error("Unable to read transcript media."));
    transaction.onerror = () =>
      reject(transaction.error || new Error("Unable to read transcript media."));
  }).finally(() => db.close());
}

async function deleteStoredMedia(key: TranscriptMediaStoreKey): Promise<void> {
  if (!canUseIndexedDb()) return;

  const db = await openMediaDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(mediaStoreName, "readwrite");
    const store = transaction.objectStore(mediaStoreName);
    const request = store.delete(key);

    request.onerror = () =>
      reject(request.error || new Error("Unable to delete transcript media."));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error || new Error("Unable to delete transcript media."));
  }).finally(() => db.close());
}

async function clearStoredMedia(): Promise<void> {
  if (!canUseIndexedDb()) return;

  const db = await openMediaDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(mediaStoreName, "readwrite");
    const store = transaction.objectStore(mediaStoreName);
    const request = store.clear();

    request.onerror = () =>
      reject(request.error || new Error("Unable to clear transcript media."));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error || new Error("Unable to clear transcript media."));
  }).finally(() => db.close());
}

function persistMedia(record: StoredTranscriptMediaRecord): void {
  void putStoredMedia(record)
    .then(() => {
      const state = useTranscriptStore.getState();
      const currentMeta =
        record.key === uploadMediaKey
          ? state.selectedFileMeta
          : state.recordedBlobMeta;
      if (currentMeta?.savedAt !== record.savedAt) {
        removeStoredMedia(record.key);
      }
    })
    .catch(() => {
      useTranscriptStore.setState((state) => {
        const currentMeta =
          record.key === uploadMediaKey
            ? state.selectedFileMeta
            : state.recordedBlobMeta;
        if (currentMeta?.savedAt !== record.savedAt) return {};

        return {
          selectedFileMeta:
            record.key === uploadMediaKey ? null : state.selectedFileMeta,
          recordedBlobMeta:
            record.key === recordMediaKey ? null : state.recordedBlobMeta,
          storageWarning: mediaPersistenceWarning,
        };
      });
    });
}

function removeStoredMedia(key: TranscriptMediaStoreKey): void {
  void deleteStoredMedia(key).catch(() => undefined);
}

export const useTranscriptStore = create<TranscriptStore>()(
  persist(
    (set, get) => ({
      ...initialTranscriptState(),
      setMode: (mode) => set({ mode }),
      setTitle: (title) => set({ title }),
      setMediaUrl: (mediaUrl) => set({ mediaUrl }),
      setSelectedFile: (file) => {
        const current = get();
        revokePreviewUrl(current.uploadPreviewUrl);

        if (!file) {
          removeStoredMedia(uploadMediaKey);
          set({
            selectedFile: null,
            selectedFileMeta: null,
            uploadPreviewUrl: "",
            isUploadPlaying: false,
          });
          return;
        }

        const canPersistMedia = canUseIndexedDb();
        const meta = mediaMetaFromBlob(file, uploadMediaKey, file.name);
        const previewUrl = createPreviewUrl(file, uploadMediaKey);
        set({
          selectedFile: file,
          selectedFileMeta: canPersistMedia ? meta : null,
          uploadPreviewUrl: previewUrl,
          isUploadPlaying: false,
          storageWarning: canPersistMedia ? "" : mediaPersistenceWarning,
        });
        if (canPersistMedia) {
          persistMedia({ ...meta, blob: file });
        }
      },
      setRecordedBlob: (blob) => {
        const current = get();
        revokePreviewUrl(current.recordPreviewUrl);

        if (!blob) {
          removeStoredMedia(recordMediaKey);
          set({
            recordedBlob: null,
            recordedBlobMeta: null,
            recordPreviewUrl: "",
          });
          return;
        }

        const canPersistMedia = canUseIndexedDb();
        const meta = mediaMetaFromBlob(blob, recordMediaKey, recordedFilename);
        const previewUrl = createPreviewUrl(blob, recordMediaKey);
        set({
          recordedBlob: blob,
          recordedBlobMeta: canPersistMedia ? meta : null,
          recordPreviewUrl: previewUrl,
          storageWarning: canPersistMedia ? "" : mediaPersistenceWarning,
        });
        if (canPersistMedia) {
          persistMedia({ ...meta, blob });
        }
      },
      setIsRecording: (isRecording) => set({ isRecording }),
      setIsUploadPlaying: (isUploadPlaying) => set({ isUploadPlaying }),
      setIsUploadMuted: (isUploadMuted) => set({ isUploadMuted }),
      setIsRecordMuted: (isRecordMuted) => set({ isRecordMuted }),
      setQuery: (query) => set({ query }),
      setValidationError: (validationError) => set({ validationError }),
      setResult: (result) => set({ result }),
      setSummary: (summary) => set({ summary }),
      hydrateStoredMedia: async () => {
        if (get().isHydratingMedia) return;
        if (get().clearExpiredTranscriptCache()) return;

        const selectedFileMeta = get().selectedFile ? null : get().selectedFileMeta;
        const recordedBlobMeta = get().recordedBlob ? null : get().recordedBlobMeta;
        if (!selectedFileMeta && !recordedBlobMeta) return;

        set({ isHydratingMedia: true, storageWarning: "" });

        try {
          const [storedUpload, storedRecord] = await Promise.all([
            selectedFileMeta ? getStoredMedia(uploadMediaKey) : Promise.resolve(null),
            recordedBlobMeta ? getStoredMedia(recordMediaKey) : Promise.resolve(null),
          ]);
          const latest = get();
          const next: Partial<TranscriptStoreState> = {
            isHydratingMedia: false,
          };

          if (
            selectedFileMeta &&
            latest.selectedFileMeta?.savedAt === selectedFileMeta.savedAt
          ) {
            revokePreviewUrl(latest.uploadPreviewUrl);
            if (storedUpload) {
              const restoredFile = fileFromStoredMedia(storedUpload);
              next.selectedFile = restoredFile;
              next.uploadPreviewUrl = createPreviewUrl(restoredFile, uploadMediaKey);
            } else {
              next.selectedFile = null;
              next.selectedFileMeta = null;
              next.uploadPreviewUrl = "";
            }
          }

          if (
            recordedBlobMeta &&
            latest.recordedBlobMeta?.savedAt === recordedBlobMeta.savedAt
          ) {
            revokePreviewUrl(latest.recordPreviewUrl);
            if (storedRecord) {
              const restoredRecord = fileFromStoredMedia(storedRecord);
              next.recordedBlob = restoredRecord;
              next.recordPreviewUrl = createPreviewUrl(restoredRecord, recordMediaKey);
            } else {
              next.recordedBlob = null;
              next.recordedBlobMeta = null;
              next.recordPreviewUrl = "";
            }
          }

          set(next);
        } catch {
          set({
            isHydratingMedia: false,
            storageWarning:
              "Saved transcript media could not be restored in this browser.",
          });
        }
      },
      clearExpiredTranscriptCache: () => {
        if (!isCacheExpired(get().cacheExpiresAt)) return false;
        get().resetTranscriptStore();
        return true;
      },
      resetTranscriptStore: () => {
        const current = get();
        revokePreviewUrl(current.recordPreviewUrl);
        revokePreviewUrl(current.uploadPreviewUrl);
        void clearStoredMedia().catch(() => undefined);
        set(initialTranscriptState());
      },
    }),
    {
      name: TRANSCRIPT_STORE_STORAGE_KEY,
      storage: createJSONStorage(() => window.localStorage),
      version: 1,
      partialize: (state): PersistedTranscriptStoreState => ({
        mode: state.mode,
        title: state.title,
        mediaUrl: state.mediaUrl,
        selectedFileMeta: state.selectedFileMeta,
        recordedBlobMeta: state.recordedBlobMeta,
        query: state.query,
        result: state.result,
        summary: state.summary,
        cacheExpiresAt: state.cacheExpiresAt,
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as
          | Partial<PersistedTranscriptStoreState>
          | undefined;
        if (!persisted || isCacheExpired(persisted.cacheExpiresAt)) {
          void clearStoredMedia().catch(() => undefined);
          return currentState;
        }

        return {
          ...currentState,
          ...persisted,
          selectedFile: null,
          recordedBlob: null,
          recordPreviewUrl: "",
          uploadPreviewUrl: "",
          isRecording: false,
          isUploadPlaying: false,
          isHydratingMedia: false,
          validationError: "",
          storageWarning: "",
        };
      },
    },
  ),
);
